import { t } from "i18next"
import { z } from "zod"
import { CASE_REFERENCE_MAX_LENGTH, MAX_GESTATION_WEEKS, textAreaDefaultMaxLength } from "@/models/common"
import { NotBillableReason, TreatmentReason } from "@/types/__generated__/union-types"

const CASE_REFERENCE_REGEX = /^([0-9]{14}|[0-9]{10}|[0-9]{9}|[0-9]{6})$/

const createChConcludeBillingSchema = () =>
  z
    .object({
      isNotBillable: z.boolean(),
      reason: z.string().optional(),
      treatmentReason: z.custom<TreatmentReason>().optional(),
      notBillableReason: z.custom<NotBillableReason>(),
      dateOfAccident: z.date().optional(),
      accidentReference: z.string().optional(),
      caseReference: z.string().max(24).optional(),
      organisationId: z.string().optional(),
      currentGestationWeek: z.string().optional().nullable(),
    })
    .superRefine(
      (
        {
          isNotBillable,
          reason,
          treatmentReason,
          notBillableReason,
          currentGestationWeek,
          dateOfAccident,
          caseReference,
          accidentReference,
        },
        ctx,
      ) => {
        if (isNotBillable && notBillableReason === "OTHER_REASON") {
          if (!reason) {
            ctx.addIssue({
              code: "custom",
              path: ["reason"],
              message: t("common.fieldErrorList.requiredField"),
            })
          } else if (reason.length > textAreaDefaultMaxLength) {
            ctx.addIssue({
              code: "custom",
              path: ["reason"],
              message: t("common.fieldErrorList.characterLimit"),
            })
          }
        }

        if (!isNotBillable) {
          if (treatmentReason === "MATERNITY") {
            if (!currentGestationWeek) {
              ctx.addIssue({
                code: "custom",
                path: ["currentGestationWeek"],
                message: t("patient.conclude.billing.currentGestationWeekRequired"),
              })
            } else if (Number(currentGestationWeek) > MAX_GESTATION_WEEKS) {
              ctx.addIssue({
                code: "custom",
                path: ["currentGestationWeek"],
                message: t("common.fieldErrorList.maxValueLimit", { max: MAX_GESTATION_WEEKS }),
              })
            }
          }

          if (!treatmentReason)
            ctx.addIssue({
              code: "custom",
              path: ["treatmentReason"],
              message: t("common.fieldErrorList.requiredField"),
            })
          else if (treatmentReason === "ACCIDENT" && !dateOfAccident) {
            ctx.addIssue({
              code: "custom",
              path: ["dateOfAccident"],
              message: t("common.fieldErrorList.requiredField"),
            })
          } else if (treatmentReason === "CONGENITAL_DEFECT") {
            if (caseReference && !CASE_REFERENCE_REGEX.test(caseReference)) {
              ctx.addIssue({
                code: "custom",
                path: ["caseReference"],
                message: t("common.fieldErrorList.caseReferenceFormat"),
              })
            }
          }

          if (
            treatmentReason === "ACCIDENT" &&
            accidentReference &&
            accidentReference.length > CASE_REFERENCE_MAX_LENGTH
          ) {
            ctx.addIssue({
              code: "custom",
              path: ["accidentReference"],
              message: t("common.fieldErrorList.accidentReferenceMaxLength", {
                maxLength: CASE_REFERENCE_MAX_LENGTH,
              }),
            })
          }
        }
      },
    )

type ChConcludeBillingSchemaType = z.infer<ReturnType<typeof createChConcludeBillingSchema>>

export { createChConcludeBillingSchema, type ChConcludeBillingSchemaType }
